import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '@/components/shell/Sidebar';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

import { familySidebarGroups } from './familySidebar';

interface FamilyRecordPendingStateProps {
  readonly guid: string;
}

/**
 * First-paint placeholder while the family detail query is in flight.
 * Mirrors `FamilyRecordView`'s layout exactly — sidebar, identity band,
 * two-column main with side rail — so the pending → loaded swap only
 * fills in content; chrome stays put.
 */
export function FamilyRecordPendingState({
  guid,
}: FamilyRecordPendingStateProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('routes.family.loading')}
      className="flex h-full"
    >
      <Sidebar groups={familySidebarGroups(guid, t)} />
      <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-clip">
        <IdentityBandSkeleton />
        <div className="flex-1 min-h-0 overflow-y-auto bg-background">
          <div className="px-8 py-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 max-w-[1400px] mx-auto">
            <div className="space-y-3 min-w-0">
              <SectionCardSkeleton headerLines={1} bodyLines={4} />
              <SectionCardSkeleton headerLines={1} bodyLines={3} />
            </div>
            <SidePanelsSkeleton />
          </div>
        </div>
      </main>
    </div>
  );
}

function IdentityBandSkeleton(): ReactElement {
  return (
    <header aria-hidden="true" className="bg-card border-b border-border">
      <div className="px-8 py-3 flex items-center gap-6">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-16 rounded-full" />
          </div>
          <Skeleton className="h-3 w-2/3 max-w-md" />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      </div>
    </header>
  );
}

function SidePanelsSkeleton(): ReactElement {
  return (
    <aside aria-hidden="true" className="hidden xl:flex flex-col gap-3">
      {[4, 2].map((rows, idx) => (
        <Card key={idx} className="overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
            <Skeleton className="h-3.5 w-3.5 rounded" />
            <Skeleton className="h-3 w-24" />
          </div>
          {Array.from({ length: rows }, (_, rowIdx) => (
            <div
              key={rowIdx}
              className="px-4 py-2.5 border-b border-border last:border-b-0 flex items-center justify-between gap-2.5"
            >
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-8" />
            </div>
          ))}
        </Card>
      ))}
    </aside>
  );
}

interface SectionCardSkeletonProps {
  readonly headerLines?: number;
  readonly bodyLines?: number;
}

function SectionCardSkeleton({
  headerLines = 1,
  bodyLines = 3,
}: SectionCardSkeletonProps): ReactElement {
  return (
    <Card aria-hidden="true">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
        {headerLines > 1 && <Skeleton className="h-3 w-1/2" />}
      </CardHeader>
      <CardContent className="space-y-2.5">
        {Array.from({ length: bodyLines }, (_, idx) => (
          <Skeleton
            key={idx}
            className={
              idx % 3 === 0
                ? 'h-3.5 w-3/4'
                : idx % 3 === 1
                  ? 'h-3.5 w-2/3'
                  : 'h-3.5 w-1/2'
            }
          />
        ))}
      </CardContent>
    </Card>
  );
}
